"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useId, useMemo, useState } from "react";
import { useTdhGrantTokensQuery } from "@/hooks/useTdhGrantTokensQuery";
import type { GrantTokensDisclosureState } from "../types";
import { getGrantTokensErrorMessage, mapTokensToRanges } from "../utils/grantTokens";
const TOKEN_PAGE_SIZE = 500;
type GrantTokensDisclosureParams = Pick<GrantTokensDisclosureState, "chain" | "contractAddress" | "grantId">;
interface UseGrantTokensDisclosureResult {
  readonly isOpen: boolean;
  readonly panelId: string;
  readonly toggleOpen: () => void;
  readonly disclosureState: GrantTokensDisclosureState;
}
export function useGrantTokensDisclosure(t0) {
  const $ = _c(30);
  const {
    chain,
    contractAddress,
    grantId
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  let t1;
  if ($[0] !== grantId || $[1] !== isOpen) {
    t1 = {
      grantId,
      pageSize: TOKEN_PAGE_SIZE,
      enabled: isOpen
    };
    $[0] = grantId;
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    tokens,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useTdhGrantTokensQuery(t1);
  let t2;
  if ($[3] !== tokens) {
    t2 = mapTokensToRanges(tokens);
    $[3] = tokens;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const tokenRanges = t2;
  const showInitialLoading = isOpen && tokenRanges.length === 0 && isLoading;
  const showInitialError = isOpen && tokenRanges.length === 0 && isError;
  let t3;
  if ($[5] !== error) {
    t3 = getGrantTokensErrorMessage(error);
    $[5] = error;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const errorMessage = t3;
  let t4;
  if ($[7] !== fetchNextPage || $[8] !== hasNextPage || $[9] !== isFetchingNextPage) {
    t4 = () => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp);
    };
    $[7] = fetchNextPage;
    $[8] = hasNextPage;
    $[9] = isFetchingNextPage;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleEndReached = t4;
  const onEndReached = hasNextPage ? handleEndReached : undefined;
  let t5;
  if ($[11] !== refetch) {
    t5 = () => {
      refetch().catch(_temp2);
    };
    $[11] = refetch;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleRetry = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsOpen(_temp3);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const toggleOpen = t6;
  let t7;
  if ($[14] !== chain || $[15] !== contractAddress || $[16] !== errorMessage || $[17] !== grantId || $[18] !== handleRetry || $[19] !== isFetchingNextPage || $[20] !== onEndReached || $[21] !== showInitialError || $[22] !== showInitialLoading || $[23] !== tokenRanges || $[24] !== tokens) {
    t7 = {
      showInitialLoading,
      showInitialError,
      tokenRanges,
      tokens,
      errorMessage,
      onRetry: handleRetry,
      contractAddress,
      chain,
      grantId,
      onEndReached,
      isFetchingNextPage
    };
    $[14] = chain;
    $[15] = contractAddress;
    $[16] = errorMessage;
    $[17] = grantId;
    $[18] = handleRetry;
    $[19] = isFetchingNextPage;
    $[20] = onEndReached;
    $[21] = showInitialError;
    $[22] = showInitialLoading;
    $[23] = tokenRanges;
    $[24] = tokens;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isOpen || $[27] !== panelId || $[28] !== t7) {
    t8 = {
      isOpen,
      panelId,
      toggleOpen,
      disclosureState: t7
    };
    $[26] = isOpen;
    $[27] = panelId;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function _temp3(previous) {
  return !previous;
}
function _temp2(err_0) {
  console.error("Failed to retry fetching grant tokens:", err_0);
}
function _temp(err) {
  console.error("Failed to fetch next page of grant tokens:", err);
}